import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "react-query";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { sendMerchantOTP, verifyMerchantOTP } from "../../../../services/merchant/merchant_auth";
import useUser from "../../../../providers/user/user_provider";
import Session from "../../../../utils/session";

interface MerchantLoginDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (merchantData: any) => void;
}

interface MerchantLoginFormInputs {
  mobileNumber: string;
  otp: string;
}


export default function MerchantLoginDialog({ open, onClose, onSuccess }: MerchantLoginDialogProps) {
  const { handleSubmit, control, reset, formState: { errors }, watch } = useForm<MerchantLoginFormInputs>();
  const { showSnackbar } = useFeedback();
  const { login } = useUser();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleClose = () => {
    if (!otpMutation.isLoading && !verifyMutation.isLoading) {
      reset();
      setLoginError(null);
      setOtpSent(false);
      setOtpSentTo("");
      setResendCooldown(0);
      onClose();
    }
  };

  const otpMutation = useMutation(sendMerchantOTP, {
    onSuccess: (data) => {
      if (data.data.success) {
        setOtpSent(true);
        const mobileNumber = watch('mobileNumber');
        setOtpSentTo(mobileNumber);
        setResendCooldown(30); // 30 seconds cooldown
        showSnackbar('OTP sent successfully!');
        
        // Start countdown timer
        const timer = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setLoginError(data.data.message);
      }
    },
    onError: (error: any) => {
      setLoginError(error?.response?.data?.error?.message || 'Failed to send OTP. Please try again.');
    },
  });

  const verifyMutation = useMutation(verifyMerchantOTP, {
    onSuccess: (data) => {
      if (data.data.success && data.data.data) {
        // Store tokens in session storage first
        Session.login(data.data.data.accessToken, data.data.data.accessToken);
        
        // Use the existing user login function to set tokens in UserProvider
        login(data.data.data.accessToken, data.data.data.accessToken);
        
        
        // Store merchant data from API response
        const merchantData = {
          merchant_id: data.data.data.merchant_id,
          name: data.data.data.name,
          state: data.data.data.state,
          type: data.data.data.type,
          accessToken: data.data.data.accessToken,
          mobile_no: watch('mobileNumber')
        };
        sessionStorage.setItem('merchant_data', JSON.stringify(merchantData));
        
        showSnackbar(`Welcome back, ${merchantData.name}!`);
        onSuccess(merchantData);
        handleClose();
      } else {
        setLoginError("Login failed. Please try again.");
      }
    },
    onError: (error: any) => {
      setLoginError(error?.response?.data?.error?.message || 'Invalid OTP. Please try again.');
    },
  });

  const handleSendOTP = () => {
    setLoginError(null);
    const mobileNumber = watch('mobileNumber');
    
    if (!mobileNumber) {
      setLoginError('Please provide mobile number');
      return;
    }

    // Validate mobile number format
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(mobileNumber)) {
      setLoginError('Please enter a valid 10-digit mobile number');
      return;
    }
    
    otpMutation.mutate({ mobileNumber });
  };

  const handleResendOTP = () => {
    if (resendCooldown > 0) return;
    handleSendOTP();
  };

  const onSubmit = (data: MerchantLoginFormInputs) => {
    setLoginError(null);
    
    // Only allow submission if OTP has been sent
    if (!otpSent) {
      setLoginError('Please send OTP first');
      return;
    }
    
    if (!data.otp) {
      setLoginError('OTP is required');
      return;
    }

    const mobileNumber = watch('mobileNumber');
    verifyMutation.mutate({ mobileNumber, otp: data.otp });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="600" textAlign="center">
          Merchant Login
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loginError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {loginError}
          </Alert>
        )}

        <Box 
          component="form" 
          onSubmit={(e) => {
            e.preventDefault();
            if (otpSent) {
              handleSubmit(onSubmit)();
            }
          }} 
          noValidate 
          sx={{ mt: 2 }}
        >
          <Controller
            name="mobileNumber"
            control={control}
            rules={{
              required: 'Mobile number is required',
              pattern: {
                value: /^[6-9]\d{9}$/,
                message: 'Please enter a valid 10-digit mobile number'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                label="Mobile Number"
                placeholder="Enter your 10-digit mobile number"
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber?.message}
                disabled={otpSent}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!otpSent) {
                      handleSendOTP();
                    }
                  }
                }}
              />
            )}
          />

          {otpSent ? (
            <>
              <Controller
                name="otp"
                control={control}
                rules={{ 
                  required: 'OTP is required',
                  pattern: {
                    value: /^\d{4}$/,
                    message: 'Please enter a valid 4-digit OTP'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Enter OTP"
                    placeholder="Enter 4-digit OTP"
                    error={!!errors.otp}
                    helperText={errors.otp?.message || `OTP sent to ${otpSentTo}`}
                    inputProps={{ maxLength: 4 }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmit(onSubmit)();
                      }
                    }}
                  />
                )}
              />
              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="text"
                  onClick={handleResendOTP}
                  disabled={resendCooldown > 0 || otpMutation.isLoading}
                  sx={{ 
                    color: resendCooldown > 0 ? "text.disabled" : "#91278F",
                    textTransform: "none"
                  }}
                >
                  {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                </Button>
              </Box>
            </>
          ) : (
            <Box mt={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleSendOTP}
                disabled={otpMutation.isLoading}
                sx={{ 
                  borderColor: "#91278F",
                  color: "#91278F",
                  '&:hover': {
                    borderColor: "#91278F",
                    backgroundColor: "#F5E6F5"
                  }
                }}
              >
                {otpMutation.isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} color="inherit" />
                    Sending OTP...
                  </Box>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </Box>
          )}

        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={otpMutation.isLoading || verifyMutation.isLoading}>
          Cancel
        </Button>
        {otpSent && (
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={verifyMutation.isLoading}
            sx={{ backgroundColor: "#91278F" }}
          >
            {verifyMutation.isLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} color="inherit" />
                Verifying...
              </Box>
            ) : (
              'Verify OTP'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
